import { apiClient } from "@/services/apiClient";
import { buildCacheKey, cachedRequest } from "@/services/requestCache";
import type {
  LearningCourseCategory,
  LearningCourseHighlightSection,
  LearningCourseItem,
  LearningCourseSidebarItem,
  LearningCoursesQuery,
  LearningCoursesResponse,
  LearningSectionItem,
} from "../types/learning";

const PUBLIC_API_CONFIG = {
  skipAuthRefresh: true,
} as const;

export async function getSectionsApi(
  locale: string,
  regionSlug?: string,
  onlyWithCourses?: boolean,
  onlyInNavbar?: boolean,
  onlyOnHomepage?: boolean
): Promise<LearningSectionItem[]> {
  // Cached: the Navbar's category menu and the page body both ask for this on
  // every public page, so without de-duplication a single navigation fired two
  // identical requests, and every subsequent navigation fired them again.
  // onlyWithCourses/onlyInNavbar/onlyOnHomepage are part of the key so the
  // student catalog's and homepage's filtered requests never collide with
  // the Navbar's own filtered one, or with each other.
  return cachedRequest(
    buildCacheKey("categories", { locale, region: regionSlug, onlyWithCourses, onlyInNavbar, onlyOnHomepage }),
    async () => {
      const response = await apiClient.get("/api/categories", {
        ...PUBLIC_API_CONFIG,
        params: {
          locale,
          region_slug: regionSlug || undefined,
          only_with_courses: onlyWithCourses ? 1 : undefined,
          only_in_navbar: onlyInNavbar ? 1 : undefined,
          only_on_homepage: onlyOnHomepage ? 1 : undefined,
        },
      });

      return normalizeSections(response.data);
    }
  );
}

export async function getCoursesApi(
  locale: string,
  query: LearningCoursesQuery = {}
): Promise<LearningCoursesResponse> {
  // The course grid is rendered by the home, courses, categories and
  // category-detail pages. Keyed on every parameter that changes the result, so
  // switching category filter or paging still hits the network.
  const key = buildCacheKey("courses", {
    locale,
    category: query.categorySlug,
    subCategory: query.subCategorySlug,
    page: query.page,
    perPage: query.perPage,
    region: query.regionSlug,
  });

  return cachedRequest(key, async () => {
    const response = await apiClient.get("/api/courses", {
      ...PUBLIC_API_CONFIG,
      params: {
        locale,
        category_slug: query.categorySlug,
        sub_category_slug: query.subCategorySlug || undefined,
        page: query.page,
        per_page: query.perPage,
        region_slug: query.regionSlug || undefined,
      },
    });

    return normalizeCourses(response.data, locale);
  });
}

export async function getCourseApi(
  locale: string,
  slug: string,
  regionSlug?: string
): Promise<LearningCourseItem> {
  const response = await apiClient.get(`/api/courses/${encodeURIComponent(slug)}`, {
    ...PUBLIC_API_CONFIG,
    headers: {
      "Accept-Language": locale,
    },
    params: {
      locale,
      region_slug: regionSlug || undefined,
    },
  });

  return normalizeCourse(response.data, locale);
}

export async function getStudentCatalogCoursesApi(
  locale: string,
  query: LearningCoursesQuery = {}
): Promise<LearningCoursesResponse> {
  const response = await apiClient.get("/api/student/catalog/courses", {
    params: {
      locale,
      category_slug: query.categorySlug,
      sub_category_slug: query.subCategorySlug || undefined,
      page: query.page,
      per_page: query.perPage,
      region_slug: query.regionSlug || undefined,
    },
  });

  return normalizeCourses(response.data, locale);
}

function normalizeSections(payload: unknown): LearningSectionItem[] {
  if (!Array.isArray(payload)) {
    throw new Error("Invalid sections response");
  }

  return payload.filter(isSectionItem).map((item) => ({
    id: item.id,
    name: item.name,
    slug: item.slug.trim(),
    image: item.image,
    // Tolerant of older responses without the field — absent means primary.
    showInOther: (item as { show_in_other?: unknown }).show_in_other === true,
    // Tolerant of older responses without the field.
    isParent: (item as { is_parent?: unknown }).is_parent === true,
    parentId: (() => {
      const parentId = (item as { parent_id?: unknown }).parent_id;
      return typeof parentId === "number" ? parentId : null;
    })(),
  }));
}

function normalizeCourses(payload: unknown, locale: string): LearningCoursesResponse {
  if (!isRecord(payload) || !Array.isArray(payload.data) || !isCoursesMeta(payload.meta)) {
    throw new Error("Invalid courses response");
  }

  return {
    data: payload.data.filter(isCourseItem).map((item) => normalizeCourse(item, locale)),
    meta: {
      currentPage: payload.meta.current_page,
      perPage: payload.meta.per_page,
      total: payload.meta.total,
      lastPage: payload.meta.last_page,
      hasMore: payload.meta.has_more,
    },
  };
}

function normalizeCourse(item: unknown, locale: string): LearningCourseItem {
  if (!isCourseItem(item)) {
    throw new Error("Invalid course response");
  }

  return {
    id: item.id,
    name: normalizeLocalizedText(item.name, locale),
    slug: item.slug.trim(),
    description: normalizeLocalizedText(item.description, locale),
    highlights: normalizeHighlights((item as { highlights?: unknown }).highlights, locale),
    sidebarItems: normalizeSidebarItems(item.sidebar_items, locale),
    image: item.image,
    video: item.video,
    hourPrice: item.hour_price,
    category: item.category
      ? {
          id: item.category.id,
          name: item.category.name,
          slug: (item.category.slug || "").trim(),
          image: item.category.image,
        }
      : null,
  };
}

function normalizeLocalizedText(value: string | { ar?: unknown; en?: unknown }, locale: string): string {
  if (typeof value === "string") {
    return value.trim();
  }

  const english = typeof value.en === "string" ? value.en.trim() : "";
  const arabic = typeof value.ar === "string" ? value.ar.trim() : "";

  return locale === "ar" ? arabic || english : english || arabic;
}

function normalizeHighlights(sections: unknown, locale: string): LearningCourseHighlightSection[] {
  if (!Array.isArray(sections)) {
    return [];
  }

  return sections
    .map((section) => {
      if (!isRecord(section)) {
        return { heading: "", items: [] };
      }

      const heading = typeof section.heading === "string"
        ? section.heading.trim()
        : normalizeLocalizedText(section.heading as { ar?: unknown; en?: unknown }, locale);

      const items = Array.isArray(section.items)
        ? section.items
            .map((item) => typeof item === "string"
              ? item.trim()
              : normalizeLocalizedText(item as { ar?: unknown; en?: unknown }, locale)
            )
            .filter((item) => item.length > 0)
        : [];

      return { heading, items };
    })
    .filter((section) => section.heading.length > 0 || section.items.length > 0);
}

function normalizeSidebarItems(items: unknown, locale: string): LearningCourseSidebarItem[] {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter(isSidebarItem).map((item) => ({
    icon: item.icon.trim(),
    title: normalizeLocalizedText(item.title, locale),
  }));
}

function isSectionItem(value: unknown): value is LearningSectionItem {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "number" &&
    (typeof value.name === "string" || isRecord(value.name)) &&
    isNonEmptyString(value.slug) &&
    (value.image === null || typeof value.image === "string")
  );
}

function isCourseCategory(value: unknown): value is LearningCourseCategory {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "number" &&
    (typeof value.name === "string" || isRecord(value.name)) &&
    (value.slug === null || typeof value.slug === "string") &&
    (value.image === null || typeof value.image === "string")
  );
}

function isCoursesMeta(
  value: unknown
): value is {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
} {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.current_page === "number" &&
    typeof value.per_page === "number" &&
    typeof value.total === "number" &&
    typeof value.last_page === "number" &&
    typeof value.has_more === "boolean"
  );
}

function isCourseItem(
  value: unknown
): value is {
  id: number;
  name: string | { ar?: unknown; en?: unknown };
  slug: string;
  description: string | { ar?: unknown; en?: unknown };
  image: string | null;
  video: string | null;
  hour_price: number;
  sidebar_items?: unknown;
  category: LearningCourseCategory | null;
} {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "number" &&
    (isNonEmptyString(value.name) || isLocalizedText(value.name)) &&
    isNonEmptyString(value.slug) &&
    (typeof value.description === "string" || isLocalizedText(value.description)) &&
    (value.image === null || typeof value.image === "string") &&
    (value.video === null || typeof value.video === "string") &&
    typeof value.hour_price === "number" &&
    (value.category === null || isCourseCategory(value.category))
  );
}

function isLocalizedText(value: unknown): value is { ar?: unknown; en?: unknown } {
  return (
    isRecord(value) &&
    (typeof value.ar === "string" || typeof value.en === "string")
  );
}

function isSidebarItem(value: unknown): value is { icon: string; title: string | { ar?: unknown; en?: unknown } } {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.icon === "string" &&
    (isNonEmptyString(value.title) || isLocalizedText(value.title))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
